"use client";

import eventClient from "@2u-landing-pages/utils/event-client";
import { useIntersectionObserver } from "@2u-landing-pages/utils/useIntersectionObserver";
import Image from "next/image";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
// @ts-ignore
import styles from "./VideoEmbed.module.scss";

// YouTube API type declarations
declare global {
  interface Window {
    YT?: {
      Player: any;
      PlayerState: {
        ENDED: 0;
        PLAYING: 1;
        PAUSED: 2;
        BUFFERING: 3;
        CUED: 5;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

export interface VideoEmbedProps {
  video_id_url?: string;
  title?: string;
  video_details?: string | object | null;
  className?: string;
  controls?: boolean;
  rootMargin?: string;
  debug?: boolean;
}

// YouTube API loader - shared across all instances
let isYouTubeAPIReady = false;
let apiLoadPromise: Promise<void> | null = null;

const loadYouTubeAPI = (): Promise<void> => {
  if (apiLoadPromise) return apiLoadPromise;

  apiLoadPromise = new Promise((resolve) => {
    if (window.YT?.Player) {
      isYouTubeAPIReady = true;
      resolve();
      return;
    }

    window.onYouTubeIframeAPIReady = () => {
      isYouTubeAPIReady = true;
      resolve();
    };

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(script);
  });

  return apiLoadPromise;
};

const extractVideoId = (input?: string): string => {
  if (!input) return "";

  // If it's already a video ID (11 characters, alphanumeric and hyphens/underscores)
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;

  // Handle various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) return match[1];
  }
  return input;
};

export const VideoEmbed: React.FC<VideoEmbedProps> = ({
  video_id_url,
  title = "Video player",
  video_details,
  className,
  controls = true,
  rootMargin = "200px",
  debug = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const trackingStateRef = useRef({
    percentWatched: 0,
    lastTime: 0,
    seekDebounceTimer: null as NodeJS.Timeout | null,
    interval: null as NodeJS.Timeout | null,
    isBuffering: false,
  });

  const extractedVideoId = extractVideoId(video_id_url);
  const reactId = useId();
  const playerMountId = useMemo(() => {
    if (!extractedVideoId) return "";
    return `youtube-player-${extractedVideoId}-${reactId}`;
  }, [extractedVideoId, reactId]);

  // Parse JSON-LD structured data
  const jsonLdContent = React.useMemo(() => {
    if (!video_details) return null;

    try {
      const jsonString = typeof video_details === "string" ? video_details : JSON.stringify(video_details);

      JSON.parse(jsonString);
      return jsonString;
    } catch (error) {
      console.warn("VideoEmbed: Invalid JSON-LD data provided", error);
      return null;
    }
  }, [video_details]);

  // Get video context for Tagular events
  const getVideoContext = useCallback((player: any) => {
    try {
      const videoData = player.getVideoData();
      return {
        videoId: videoData.video_id,
        videoTitle: videoData.title,
        sequenceType: "single",
        videoPlayer: "Youtube",
        videoLength: Math.round(player.getDuration()),
        playheadPosition: Math.round(player.getCurrentTime()),
        volumeLevel: player.isMuted() ? 0 : player.getVolume(),
        autoplay: false,
      };
    } catch (error) {
      console.error("VideoEmbed: Error getting video context", error);
      return null;
    }
  }, []);

  // Track video events via event-client (Tagular beam)
  const trackEvent = useCallback(
    (eventType: string, additionalData?: { videoWatched?: number; videoWatchedTime?: number }) => {
      if (!playerRef.current) {
        if (debug) console.warn("[VideoEmbed] No player instance");
        return;
      }

      const videoContext = getVideoContext(playerRef.current);
      if (!videoContext) {
        if (debug) console.warn("[VideoEmbed] Could not get video context");
        return;
      }

      const payload = { videoContext, ...additionalData };

      if (debug) {
        console.log("[VideoEmbed] Tracking event:", eventType, payload);
      }

      switch (eventType) {
        case "core.videoWatched.v1":
          eventClient.videoWatched({
            videoContext,
            videoWatched: additionalData?.videoWatched ?? 0,
            videoWatchedTime: additionalData?.videoWatchedTime ?? 0,
          });
          break;
        case "core.videoSeekStarted.v1":
          eventClient.videoSeekStarted(payload);
          break;
        case "core.videoSeekCompleted.v1":
          eventClient.videoSeekCompleted(payload);
          break;
        case "core.videoCompleted.v1":
          eventClient.videoCompleted(payload);
          break;
        case "core.videoPlayed.v1":
          eventClient.videoPlayed(payload);
          break;
        case "core.videoPaused.v1":
          eventClient.videoPaused(payload);
          break;
        case "core.videoInterrupted.v1":
          eventClient.videoInterrupted(payload);
          break;
        default:
          if (debug) console.warn("[VideoEmbed] Unknown event type:", eventType);
      }
    },
    [getVideoContext, debug],
  );

  // Track watched percentages
  const trackWatchedPercentages = useCallback(() => {
    if (!playerRef.current || trackingStateRef.current.isBuffering) return;

    try {
      const duration = playerRef.current.getDuration();
      const currentTime = playerRef.current.getCurrentTime();
      const percentage = (currentTime / duration) * 100;

      // Track highest percentage only
      if (percentage >= 99 && trackingStateRef.current.percentWatched < 100) {
        trackingStateRef.current.percentWatched = 100;
        trackEvent("core.videoWatched.v1", {
          videoWatched: 100,
          videoWatchedTime: Math.floor(currentTime),
        });
      } else if (percentage >= 75 && trackingStateRef.current.percentWatched < 75) {
        trackingStateRef.current.percentWatched = 75;
        trackEvent("core.videoWatched.v1", {
          videoWatched: 75,
          videoWatchedTime: Math.floor(currentTime),
        });
      } else if (percentage >= 50 && trackingStateRef.current.percentWatched < 50) {
        trackingStateRef.current.percentWatched = 50;
        trackEvent("core.videoWatched.v1", {
          videoWatched: 50,
          videoWatchedTime: Math.floor(currentTime),
        });
      } else if (percentage >= 25 && trackingStateRef.current.percentWatched < 25) {
        trackingStateRef.current.percentWatched = 25;
        trackEvent("core.videoWatched.v1", {
          videoWatched: 25,
          videoWatchedTime: Math.floor(currentTime),
        });
      }
    } catch (error) {
      console.error("VideoEmbed: Error tracking watched percentages", error);
    }
  }, [trackEvent]);

  // Handle seek detection with debouncing
  const handleSeekDetection = useCallback(
    (currentTime: number) => {
      const timeDiff = Math.abs(currentTime - trackingStateRef.current.lastTime);

      // If time jump is greater than 1 second, it's a seek
      if (timeDiff > 1) {
        // Clear existing debounce timer
        if (trackingStateRef.current.seekDebounceTimer) {
          clearTimeout(trackingStateRef.current.seekDebounceTimer);
        }

        // Track seek started immediately
        trackEvent("core.videoSeekStarted.v1");

        // Debounce seek completed (500ms)
        trackingStateRef.current.seekDebounceTimer = setTimeout(() => {
          trackEvent("core.videoSeekCompleted.v1");
        }, 500);
      }

      trackingStateRef.current.lastTime = currentTime;
    },
    [trackEvent],
  );

  // YouTube player state change handler
  const onPlayerStateChange = useCallback(
    (event: any) => {
      const state = event.data;

      if (state === window.YT?.PlayerState.ENDED) {
        // Video completed
        if (trackingStateRef.current.interval) {
          clearInterval(trackingStateRef.current.interval);
          trackingStateRef.current.interval = null;
        }
        trackEvent("core.videoCompleted.v1");
        // Reset tracking state
        trackingStateRef.current.percentWatched = 0;
        trackingStateRef.current.lastTime = 0;
      } else if (state === window.YT?.PlayerState.PLAYING) {
        // Video playing
        trackingStateRef.current.isBuffering = false;

        // Check for seek (only if player is fully initialized)
        if (playerRef.current?.getCurrentTime) {
          const currentTime = playerRef.current.getCurrentTime();
          handleSeekDetection(currentTime);
        }

        // Start tracking interval
        if (!trackingStateRef.current.interval) {
          trackingStateRef.current.interval = setInterval(() => {
            trackWatchedPercentages();
            // Update last time for seek detection
            if (playerRef.current?.getCurrentTime) {
              trackingStateRef.current.lastTime = playerRef.current.getCurrentTime();
            }
          }, 1000);
        }

        trackEvent("core.videoPlayed.v1");
      } else if (state === window.YT?.PlayerState.PAUSED) {
        // Video paused
        if (trackingStateRef.current.interval) {
          clearInterval(trackingStateRef.current.interval);
          trackingStateRef.current.interval = null;
        }
        trackEvent("core.videoPaused.v1");
      } else if (state === window.YT?.PlayerState.BUFFERING) {
        // Pause tracking during buffering
        trackingStateRef.current.isBuffering = true;
        if (trackingStateRef.current.interval) {
          clearInterval(trackingStateRef.current.interval);
          trackingStateRef.current.interval = null;
        }
      }
    },
    [trackEvent, handleSeekDetection, trackWatchedPercentages],
  );

  // Initialize YouTube player
  const initializePlayer = useCallback(async () => {
    if (!containerRef.current || playerRef.current) return;

    try {
      await loadYouTubeAPI();

      if (!window.YT) {
        throw new Error("YouTube API failed to load");
      }

      if (!playerMountId) {
        throw new Error("VideoEmbed: playerMountId is empty");
      }

      playerRef.current = new window.YT.Player(playerMountId, {
        videoId: extractedVideoId,
        playerVars: {
          controls: controls ? 1 : 0,
          modestbranding: 1,
          rel: 0,
        },
        events: {
          onReady: () => {
            setShowThumbnail(false);
          },
          onStateChange: onPlayerStateChange,
        },
      });
    } catch (error) {
      console.error("VideoEmbed: Failed to initialize YouTube player", error);
    }
  }, [extractedVideoId, controls, playerMountId, onPlayerStateChange]);

  const handleIntersect = useCallback((element: HTMLElement) => {
    setIsLoaded(true);
  }, []);

  const addElement = useIntersectionObserver(handleIntersect, {
    rootMargin,
    threshold: 0,
  });

  // Initialize player when loaded
  useEffect(() => {
    if (isLoaded) initializePlayer();
  }, [isLoaded, initializePlayer]);

  // Handle page visibility (background tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && playerRef.current) {
        try {
          // Pause video when tab goes to background
          const playerState = playerRef.current.getPlayerState();
          if (window.YT && playerState === window.YT.PlayerState.PLAYING) {
            playerRef.current.pauseVideo();
            trackEvent("core.videoInterrupted.v1");
          }
        } catch (error) {
          console.error("VideoEmbed: Error handling visibility change", error);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [trackEvent]);

  // Cleanup on unmount
  useEffect(() => {
    const trackingState = trackingStateRef.current;
    const player = playerRef.current;

    return () => {
      if (trackingState.interval) {
        clearInterval(trackingState.interval);
      }
      if (trackingState.seekDebounceTimer) {
        clearTimeout(trackingState.seekDebounceTimer);
      }
      if (player?.destroy) {
        try {
          player.destroy();
        } catch (error) {
          console.error("VideoEmbed: Error destroying player", error);
        }
      }
    };
  }, []);

  if (!extractedVideoId) return null;

  const thumbnailUrl = `https://img.youtube.com/vi/${extractedVideoId}/hqdefault.jpg`;

  return (
    <>
      {jsonLdContent && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdContent }} />}
      <div
        ref={(el) => {
          containerRef.current = el;
          if (el) {
            addElement(el);
          }
        }}
        className={`${styles.container} ${className || ""}`}
      >
        {showThumbnail && (
          <div className={styles.placeholder}>
            <Image
              src={thumbnailUrl}
              alt={title}
              fill
              className={styles.thumbnail}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              unoptimized
            />
          </div>
        )}
        {isLoaded && playerMountId && <div id={playerMountId} className={styles.iframe} />}
      </div>
    </>
  );
};
